export async function generateQuotePDF(form, price, PRIMARY = "#0f1943", LOGO_URL, sendEmail = false, i18n = {}) {
  const locale = i18n.locale || "it-IT";
  const language = i18n.language || "it";
  const common = i18n.common || {};
  const labels = {
    it: {
      title: "Preventivo Assicurazione Evento - Wedly",
      eventDetails: "Dettagli evento",
      holderDetails: "Dati intestatario",
      field: "Campo",
      value: "Valore",
      eventDate: "Data evento",
      location: "Location",
      insuredAmount: "Importo assicurato",
      rainThreshold: "Soglia pioggia",
      time: "Orario",
      premium: "Premio assicurativo",
      indemnity: "Indennizzo (70%)",
      supplierCode: "Codice fornitore",
      weddingOf: "Matrimonio di",
      firstName: "Nome",
      lastName: "Cognome",
      phone: "Telefono",
      fiscalCode: "Codice Fiscale",
      gender: "Genere",
      birthDate: "Data di nascita",
      birthPlace: "Luogo di nascita",
      residence: "Residenza",
      footer: "© Wedly — Proteggi il tuo giorno speciale | wedly.it",
      fileName: "preventivo-wedly",
      sent: "Preventivo inviato all'agenzia assicurativa con successo.",
      sendError: "Errore durante l'invio della mail. Riprova.",
    },
    en: {
      title: "Event Insurance Quote - Wedly",
      eventDetails: "Event details",
      holderDetails: "Policyholder details",
      field: "Field",
      value: "Value",
      eventDate: "Event date",
      location: "Location",
      insuredAmount: "Insured amount",
      rainThreshold: "Rainfall threshold",
      time: "Time",
      premium: "Insurance premium",
      indemnity: "Payout (70%)",
      supplierCode: "Supplier code",
      weddingOf: "Wedding of",
      firstName: "First name",
      lastName: "Last name",
      phone: "Phone",
      fiscalCode: "Tax code",
      gender: "Gender",
      birthDate: "Date of birth",
      birthPlace: "Place of birth",
      residence: "Residence",
      footer: "© Wedly — Protect your special day | wedly.it",
      fileName: "wedly-quote",
      sent: "Quote successfully sent to the insurance agency.",
      sendError: "Error while sending the email. Please try again.",
    },
  }[language] || {};

  if (!window.jspdf || !window.jspdf.jsPDF) {
    alert(common.pdfNotReady || "Libreria PDF non ancora pronta. Riprova tra qualche secondo.");
    return;
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  // === Fascia blu con logo ===
  doc.setFillColor(PRIMARY);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 80, "F");

  // === Logo ===
  if (LOGO_URL) {
    try {
      const img = await fetch(LOGO_URL).then((r) => r.blob());
      const reader = await new Promise((res) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.readAsDataURL(img);
      });
      doc.addImage(reader, "PNG", 24, 20, 160, 40, undefined, "FAST");
    } catch {
      console.warn("Logo non caricato, continuo senza immagine.");
    }
  }

  // === Titolo ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor("#ffffff");
  doc.text(labels.title, 210, 46);

  // === Utility ===
  const formatCurrency = (v) => Number(v || 0).toLocaleString(locale, { style: "currency", currency: "EUR" });
  const formatHour = (h) =>
    h && /^\d{1,2}:\d{2}$/.test(h)
      ? h.split(":").map((p) => p.padStart(2, "0")).join(":")
      : h || "-";
  const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    const b = parseInt(h, 16);
    return [(b >> 16) & 255, (b >> 8) & 255, b & 255];
  };

  // === Tabelle ===
  const eventRows = [
    [labels.eventDate, new Date(form.start_date).toLocaleDateString(locale)],
    [labels.location, form.location || "-"],
    [labels.insuredAmount, formatCurrency(form.amount)],
    [labels.rainThreshold, form.amount_rain || "-"],
    [labels.time, `${formatHour(form.start_hour)} - ${formatHour(form.end_hour)}`],
    [labels.premium, price != null ? formatCurrency(price) : "-"],
    [labels.indemnity, formatCurrency(Number(form.amount || 0) * 0.7)],
  ];

  const userRows = [
    [labels.supplierCode, form.supplier_code || "-"],
    [labels.weddingOf, form.wedding_of || "-"],
    [labels.firstName, form.first_name || "-"],
    [labels.lastName, form.last_name || "-"],
    ["Email", form.email || "-"],
    [labels.phone, form.phone || "-"],
    [labels.fiscalCode, form.fiscal_code || "-"],
    [labels.gender, form.gender || "-"],
    [labels.birthDate, form.birth_date || "-"],
    [labels.birthPlace, form.birth_country || "-"],
    [labels.residence, form.residence || "-"],
    ["CAP", form.cap || "-"],
  ];

  // === Tabelle PDF ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(PRIMARY);
  doc.text(labels.eventDetails, 24, 120);

  doc.autoTable({
    startY: 135,
    head: [[labels.field, labels.value]],
    body: eventRows,
    styles: { fontSize: 11, cellPadding: 6 },
    headStyles: { fillColor: hexToRgb(PRIMARY), textColor: "#fff" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 24, right: 24 },
  });

  const afterEvent = doc.lastAutoTable.finalY + 25;
  doc.text(labels.holderDetails, 24, afterEvent);

  doc.autoTable({
    startY: afterEvent + 12,
    head: [[labels.field, labels.value]],
    body: userRows,
    styles: { fontSize: 11, cellPadding: 6 },
    headStyles: { fillColor: hexToRgb(PRIMARY), textColor: "#fff" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 24, right: 24 },
  });

  // === Footer ===
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(10);
  doc.setTextColor("#666");
  doc.text(
    labels.footer,
    doc.internal.pageSize.getWidth() / 2,
    pageHeight - 20,
    { align: "center" }
  );

  // === Solo scarica ===
  if (!sendEmail) {
    doc.save(`${labels.fileName}-${form.start_date || "evento"}.pdf`);
    return;
  }

  // === Invia via email ===
  const pdfBase64 = doc.output("datauristring");

  try {
    const res = await fetch("https://wedly.it/wp-json/api/sendB2BQuote", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        price,
        pdf_base64: pdfBase64,
      }),
    });

    const data = await res.json();
    if (data?.code === 200) {
      alert(labels.sent);
    } else {
      alert(labels.sendError);
    }
  } catch (err) {
    console.error(err);
    alert(common.connectionError || "Errore di connessione.");
  }
}
